import React from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useTranslation } from "react-i18next";
import { Task } from "../types";
import { TaskStatusKey, taskStatusColors } from "../utils/statusColors";
import { User } from "../types";
import dayjs, { Dayjs } from "dayjs";
import { DEVICE_TZ, fromServerToLocal } from "../utils/time";

interface TaskFormProps {
  form: Partial<Task>;
  users: User[];
  isEditing: boolean;
  handleTextChange: (
    field: keyof Partial<Task>,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (
    field: keyof Partial<Task>,
  ) => (e: SelectChangeEvent<TaskStatusKey | string>) => void;
  handleDateChange: (
    field: keyof Partial<Task>,
  ) => (newValue: Dayjs | null) => void;
}

const TaskForm: React.FC<TaskFormProps> = ({
  form,
  users,
  isEditing,
  handleTextChange,
  handleSelectChange,
  handleDateChange,
}) => {
  const { t } = useTranslation();
  const statusTranslations: Record<TaskStatusKey, string> = {
    "Not Started": t("statuses.task.not_started"),
    Started: t("statuses.task.started"),
    Completed: t("statuses.task.completed"),
    Deferred: t("statuses.task.deferred"),
    Сanceled: t("statuses.task.canceled"),
  };

  const invalidStart = !!form.dateStart && !dayjs(form.dateStart).isValid();
  const invalidEnd = !!form.dateEnd && !dayjs(form.dateEnd).isValid();

  return (
    <Box component="form" noValidate sx={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" gap={0}>
        <TextField
          label={t("tasks.detail.nameLabel")}
          fullWidth
          margin="normal"
          value={form.name || ""}
          onChange={handleTextChange("name")}
          disabled={!isEditing}
          size="small"
          required
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 0,
          }}
        >
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel
              sx={{
                color: form.status
                  ? taskStatusColors[form.status as TaskStatusKey]?.color
                  : "inherit",
                "&.Mui-focused": {
                  color: form.status
                    ? taskStatusColors[form.status as TaskStatusKey]?.color
                    : "inherit",
                },
              }}
            >
              {t("tasks.detail.statusLabel")}
            </InputLabel>
            <Select
              label={t("tasks.detail.statusLabel")}
              value={form.status || "Not Started"}
              onChange={handleSelectChange("status")}
              disabled={!isEditing}
              sx={{
                color: form.status
                  ? taskStatusColors[form.status as TaskStatusKey]?.color
                  : "inherit",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: form.status
                    ? taskStatusColors[form.status as TaskStatusKey]?.color
                    : "inherit",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: form.status
                    ? taskStatusColors[form.status as TaskStatusKey]?.color
                    : "inherit",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: form.status
                    ? taskStatusColors[form.status as TaskStatusKey]?.color
                    : "inherit",
                },
                "&.Mui-disabled": {
                  color: form.status
                    ? taskStatusColors[form.status as TaskStatusKey]?.color
                    : "inherit",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: form.status
                      ? taskStatusColors[form.status as TaskStatusKey]?.color
                      : "inherit",
                  },
                },
              }}
            >
              {Object.keys(statusTranslations).map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  sx={{
                    color: taskStatusColors[status as TaskStatusKey].color,
                  }}
                >
                  {statusTranslations[status as TaskStatusKey]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>{t("tasks.detail.assigneeLabel")}</InputLabel>
            <Select
              label={t("tasks.detail.assigneeLabel")}
              value={form.assignedUserId || "fallback-user-id"}
              onChange={handleSelectChange("assignedUserId")}
              disabled={!isEditing}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 0,
            alignItems: "flex-start",
          }}
        >
          <DateTimePicker
            label={t("tasks.detail.startDateLabel")}
            value={fromServerToLocal(form.dateStart)}
            onChange={(newValue) => handleDateChange("dateStart")(newValue)}
            ampm={false}
            disabled={!isEditing}
            timezone={DEVICE_TZ as any}
            reduceAnimations={true}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal",
                size: "small",
                error: invalidStart,
                helperText: invalidStart
                  ? t("tasks.detail.invalidStartDate")
                  : "",
              },
              actionBar: {
                actions: ["today", "cancel", "accept"],
                sx: {
                  "& .MuiButton-root": {
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color: "#fff",
                    minWidth: 80,
                  },
                  "& .MuiButton-root:nth-of-type(1)": {
                    // Сегодня
                    backgroundColor: "#4467A5",
                    "&:hover": {
                      backgroundColor: "#385894",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(2)": {
                    // Отмена
                    backgroundColor: "#b71c1c",
                    "&:hover": {
                      backgroundColor: "#9a0007",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(3)": {
                    // Сохранить
                    backgroundColor: "#2e7d32",
                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                  },
                },
              },
            }}
            sx={{ flex: 1 }}
          />

          <DateTimePicker
            label={t("tasks.detail.endDateLabel")}
            value={fromServerToLocal(form.dateEnd)}
            onChange={(newValue) => handleDateChange("dateEnd")(newValue)}
            ampm={false}
            disabled={!isEditing}
            timezone={DEVICE_TZ as any}
            reduceAnimations={true}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal",
                size: "small",
                error: invalidEnd,
                helperText: invalidEnd ? t("tasks.detail.invalidEndDate") : "",
              },
              actionBar: {
                actions: ["today", "cancel", "accept"],
                sx: {
                  "& .MuiButton-root": {
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color: "#fff",
                    minWidth: 80,
                  },
                  "& .MuiButton-root:nth-of-type(1)": {
                    // Сегодня
                    backgroundColor: "#4467A5",
                    "&:hover": {
                      backgroundColor: "#385894",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(2)": {
                    // Отмена
                    backgroundColor: "#b71c1c",
                    "&:hover": {
                      backgroundColor: "#9a0007",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(3)": {
                    // Сохранить
                    backgroundColor: "#2e7d32",
                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                  },
                },
              },
            }}
            sx={{ flex: 1 }}
          />
        </Box>

        <TextField
          label={t("tasks.detail.descriptionLabel")}
          fullWidth
          margin="normal"
          multiline
          minRows={3}
          value={form.description || ""}
          onChange={handleTextChange("description")}
          disabled={!isEditing}
          size="small"
        />

        {form.status === "Completed" && (
          <>
            <TextField
              label={t("tasks.detail.resolutionLabel")}
              fullWidth
              margin="normal"
              multiline
              minRows={3}
              value={form.resolution || ""}
              onChange={handleTextChange("resolution")}
              disabled={!isEditing}
              size="small"
              required
              error={form.status === "Completed" && !form.resolution}
              helperText={
                form.status === "Completed" && !form.resolution
                  ? t("tasks.detail.requiredResolution")
                  : ""
              }
            />

            <TextField
              label={t("tasks.detail.completedDateLabel")}
              fullWidth
              margin="normal"
              value={
                form.dateCompleted
                  ? fromServerToLocal(form.dateCompleted)?.format(
                      "DD.MM.YYYY HH:mm",
                    ) || ""
                  : ""
              }
              disabled
              size="small"
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default TaskForm;
